import { useEffect, useRef } from "react";
import { Check, X } from "lucide-react";

const cinzel = "font-['Cinzel',serif]";

/** The filter groups themselves (used in the desktop sidebar and inside the mobile drawer). */
export function FilterGroups({ groups, filters, onToggle, onClearGroup, idPrefix }) {
  return (
    <div className="space-y-6">
      {groups.map((group) => {
        const selected = filters[group.key] || [];
        return (
          <fieldset key={group.key} className="min-w-0">
            <div className="mb-3 flex items-center justify-between">
              <legend className={`${cinzel} text-[11px] font-bold uppercase tracking-[0.2em] text-[#a07828]`}>
                {group.label}
              </legend>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => onClearGroup(group.key)}
                  className="font-['Raleway',sans-serif] text-[11px] text-[#6b5535] underline decoration-[#c9a84c]/50 underline-offset-2 hover:text-[#a07828]"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {group.options.map((opt) => {
                const checked = selected.includes(opt.value);
                const id = `${idPrefix}-${group.key}-${opt.value}`;
                return (
                  <label key={opt.value} htmlFor={id} className="cursor-pointer">
                    <input
                      id={id}
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(group.key, opt.value)}
                      className="peer sr-only"
                    />
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-['Raleway',sans-serif] text-[12px] font-medium transition-all duration-200 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#c9a84c] motion-reduce:transition-none ${
                        checked
                          ? "border-transparent bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] text-[#1a0f02] shadow-[0_4px_14px_rgba(201,168,76,0.3)]"
                          : "border-[#c9a84c]/30 bg-white text-[#6b5535] hover:border-[#c9a84c]/70 hover:text-[#1a0f02]"
                      }`}
                    >
                      {checked && <Check size={12} strokeWidth={3} aria-hidden="true" />}
                      {opt.label}
                      <span className={checked ? "text-[#1a0f02]/60" : "text-[#6b5535]/60"}>({opt.count})</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}

/** Desktop sidebar panel */
export default function ResultsFilters({ groups, filters, onToggle, onClearGroup, onClearAll, activeCount, count, total }) {
  return (
    <aside
      aria-label="Filter results"
      className="rounded-2xl border border-[#c9a84c]/25 bg-white/70 p-5 shadow-[0_2px_4px_rgba(90,60,10,0.05),0_8px_24px_rgba(90,60,10,0.06)] backdrop-blur-sm"
    >
      <div className="mb-5 flex items-center justify-between">
        <h3 className={`${cinzel} text-[13px] font-bold uppercase tracking-[0.14em] text-[#1a0f02]`}>Filters</h3>
        <button
          type="button"
          onClick={onClearAll}
          disabled={activeCount === 0}
          className="font-['Raleway',sans-serif] text-[12px] font-medium text-[#a07828] underline decoration-[#c9a84c]/50 underline-offset-2 transition-opacity hover:text-[#1a0f02] disabled:cursor-default disabled:no-underline disabled:opacity-40"
        >
          Clear All Filters
        </button>
      </div>

      <FilterGroups groups={groups} filters={filters} onToggle={onToggle} onClearGroup={onClearGroup} idPrefix="side" />

      <p className="mt-6 border-t border-[#c9a84c]/20 pt-4 font-['Raleway',sans-serif] text-[12px] text-[#6b5535]">
        Showing <strong className="text-[#1a0f02]">{count}</strong> of {total} results
      </p>
    </aside>
  );
}

/** Mobile / tablet bottom-sheet drawer */
export function FilterDrawer({ open, onClose, groups, filters, onToggle, onClearGroup, onClearAll, activeCount, count }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll('button:not([disabled]), input, [href]');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, [open]);

  return (
    <div
      className={`fixed inset-0 z-[70] lg:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
      aria-hidden={!open}
    >
      {/* backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-[#0e0905]/70 backdrop-blur-[2px] transition-opacity duration-300 motion-reduce:transition-none ${open ? "opacity-100" : "opacity-0"}`}
      />

      {/* sheet */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filter results"
        className={`absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col rounded-t-[24px] border-t border-[#c9a84c]/40 bg-[#f5ede0] shadow-[0_-20px_60px_rgba(0,0,0,0.4)] transition-transform duration-300 ease-out motion-reduce:transition-none ${open ? "translate-y-0" : "translate-y-full"}`}
        style={{ visibility: open ? "visible" : "hidden", transitionProperty: "transform, visibility" }}
      >
        <div className="flex items-center justify-between border-b border-[#c9a84c]/25 px-5 py-4">
          <h3 className={`${cinzel} text-[14px] font-bold uppercase tracking-[0.14em] text-[#1a0f02]`}>Filters</h3>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#c9a84c]/40 bg-white text-[#1a0f02]"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5" style={{ WebkitOverflowScrolling: "touch" }}>
          <FilterGroups groups={groups} filters={filters} onToggle={onToggle} onClearGroup={onClearGroup} idPrefix="drawer" />
        </div>

        <div
          className="flex items-center gap-3 border-t border-[#c9a84c]/25 bg-[#f5ede0] px-5 pt-4"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <button
            type="button"
            onClick={onClearAll}
            disabled={activeCount === 0}
            className={`${cinzel} flex-1 rounded-full border border-[#c9a84c]/50 bg-white py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[#a07828] disabled:opacity-40`}
          >
            Clear All
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`${cinzel} flex-[1.4] rounded-full bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[#1a0f02]`}
          >
            Show {count} {count === 1 ? "result" : "results"}
          </button>
        </div>
      </div>
    </div>
  );
}